import styled from "styled-components"
import { Stack } from "@fastly/consistently"
import { StyledMikanSpan } from "@components/Mikan/Mikan.styled"

/* Extendable styles */

export const StyledMain = styled.main`
  background: white;
`
export const StyledSection = styled.section`
  width: 100%;
  padding: 0 1rem;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;

  & p:empty {
    display: none;
  }
`
export const StyledA = `
  font-weight: 600;
  text-decoration: none !important;

  &:before {
    right: 3px;
    top: 1px;
  }
`

/* Hero */
export const HeroHomePage = styled(Stack)`
  & p:empty {
    display: none;
  }

  @media (min-width: 544px) {
    flex-flow: column-reverse;
  }
  @media (min-width: 992px) {
    flex-flow: inherit;
  }
`
export const HeroHomePageText = styled.div`
  @media (min-width: 992px) {
    width: 615px;
  }

  & > div {
    margin-bottom: 1.5rem;
  }

  & > p {
    margin-bottom: 1.75rem;
    font: var(--text-lg);
  }

  & span {
    @media (max-width: 992px) {
      font-size: 2.8625rem;
    }
  }
`
export const HeroHomePageImage = styled.div`
  position: relative;
  z-index: 1;
  display: none;
  height: 0;

  @media (min-width: 768px) {
    height: auto;
    display: block;

    img {
      height: auto;
    }

    & > img {
      margin-top: ${({ theme }) => theme.space["3xl"]};
      z-index: 2;
      margin-bottom: 1.5rem;
      max-width: inherit;
      object-fit: contain;
    }
  }
`

/* Features */
export const StyledIntroFeatures = styled.div`
  width: 80%;
  margin-bottom: 48px;

  @media (min-width: 768px) {
    width: 62%;
  }

  @media (min-width: 1024px) {
    width: 50%;
    max-width: 672px;
  }
`
export const StyledFeatures = styled(StyledSection)``

export const StyledFeaturesListCard = styled.div`
  width: 100%;
  margin-bottom: ${({ theme }) => theme.space.xl};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
`

export const StyledFeaturesCard = styled.div`
  width: 100%;
  background: var(--white);
  box-shadow: 0px 16px 48px rgba(0, 0, 0, 0.06);
  border-radius: 16px;
  padding: 32px 16px 50px 16px;

  @media (min-width: 768px) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    padding: 32px;
    grid-column-gap: ${({ theme }) => theme.space["4xl"]};
    grid-auto-flow: dense;

    .right {
      order: 1;
    }
    .left {
      order: 0;
    }
  }

  @media (min-width: 1024px) {
    padding: 54px 96px;
  }

  @media (min-width: 1440px) {
    max-width: 1134px;
  }
`
export const StyledFeaturesCardImg = styled.div`
  margin-bottom: 60px;

  @media (min-width: 768px) {
    margin-bottom: 0;
    align-self: center;
  }

  .img-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;

    .example {
      background: var(--grey-30);
      border-radius: 4px;
      padding: 7.5px;
      position: absolute;
      bottom: -11%;

      @media (min-width: 500px) {
        bottom: -6%;
      }

      @media (min-width: 768px) {
        bottom: -11%;
      }

      @media (min-width: 910px) {
        bottom: -6%;
      }

      @media (min-width: 1024px) {
        bottom: -6%;
        right: -5%;
      }

      span {
        font: var(--text-sm);
        line-height: 15px;
      }

      > span:nth-child(2) {
        text-decoration: underline;
        color: var(--blue-dark);
      }

      ${StyledMikanSpan} {
        display: inline-block;
        white-space: pre;
      }
    }
  }
`
export const StyledFeaturesCardDescription = styled.div`
  width: 90%;

  @media (min-width: 1024px) {
    width: 95%;
    align-self: center;
  }

  h4 {
    position: relative;
    margin-bottom: 20px;
    ${(props) => props.locale === "ja" && "white-space: pre;"};
  }

  h4[id="so-long-status-quo-cdns"]:before {
    top: -4px;
  }

  h4[id="seriously-useful-security"]:before {
    top: -5px;
  }

  h4[id="all-the-powers-of-edge-computing"]:before {
    top: 6px;

    @media (min-width: 442px) {
      top: -4px;
    }
    @media (min-width: 768px) {
      top: 6px;
    }
    @media (min-width: 886px) {
      top: -4px;
    }
  }

  p {
    font: var(--text-md);
    color: var(--grey-70);

    sup {
      font: var(--text-sm);
    }
  }

  > p:last-of-type {
    margin: 0;
  }

  ul {
    list-style: none;
    padding-left: 1.5rem;

    @media (min-width: 1024px) {
      display: grid;
      grid-template-columns: 1fr 1fr;
      grid-column-gap: 2.55rem;
    }

    li {
      position: relative;

      p {
        display: inline-block;
        margin-bottom: 0.5rem;
      }

      &::before {
        content: "";
        display: inline-block;
        height: 0.45rem;
        width: 0.45rem;
        background-color: ${({ color }) => `var(--${color})`};
        border-radius: 1rem;
        margin-left: -1.5em;
        position: absolute;
        top: 0.5rem;
        left: 0;
      }
    }

    li:last-of-type {
      p {
        margin-bottom: 0;
      }
    }
  }

  a {
    ${StyledA}
  }
`

/* Trusted */
export const StyledTrusted = styled(StyledSection)`
  margin-bottom: 128px;

  > h2 {
    margin-bottom: 40px;

    @media (min-width: 768px) {
      margin-bottom: 0px;
    }
  }
`
export const StyledIntroTrusted = styled.div`
  margin-bottom: 56px;

  @media (min-width: 768px) {
    margin-bottom: 20px;
  }
`
export const StyledTrustedWrapper = styled.div`
  width: 100%;
  max-width: 1140px;

  @media (min-width: 768px) {
    display: grid;
    gap: 16px;
    grid-template-rows: 1fr auto;
    grid-template-columns: 1fr auto 1fr;
  }

  > div:nth-of-type(1) {
    min-height: 215px;

    @media (min-width: 768px) {
      max-height: 200px;
    }

    @media (min-width: 1024px) {
      justify-self: end;
    }
  }

  > div:nth-of-type(3) {
    min-height: 117px;

    @media (min-width: 768px) {
      max-height: 212px;
    }

    @media (min-width: 1024px) {
      justify-self: start;
    }
  }
`
export const StyledTrustedContent = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: space-between;
  width: 100%;

  @media (min-width: 768px) {
    margin: 21% 0 16% 0;
  }

  @media (min-width: 1024px) {
    margin: 21% 0 16% 0;
  }

  @media (min-width: 1440px) {
    width: 100%;
    margin: 10% 0;
  }

  h4 {
    grid-row: 1/1;
    text-align: center;
    letter-spacing: -0.24px;
    color: var(--grey-90);
  }

  h4:first-of-type {
    margin-bottom: 30px;

    @media (min-width: 768px) {
      margin-bottom: 0px;
    }
  }

  p {
    display: none;
  }

  > div {
    display: flex;
    align-items: center;
    justify-content: space-between;
    grid-row: 2/2;
  }

  div.g2 {
    display: grid;
    gap: 16px;
    grid-template-columns: repeat(3, 1fr);
    justify-content: space-around;
    width: 100%;
    margin: 0 0 35px 0;

    @media (min-width: 768px) {
      margin: 0;
    }

    img {
      height: 104px;
      width: auto;

      @media (min-width: 768px) {
        height: 95px;
      }

      @media (min-width: 1440px) {
        height: 104px;
      }
    }
  }

  div.analyst {
    width: 95%;
    max-width: 343px;
    min-height: 117px;

    @media (min-width: 768px) {
      width: 80%;
    }

    @media (min-width: 1024px) {
      width: 70%;
    }

    @media (min-width: 1440px) {
      width: 80%;
    }

    img {
      height: 38px;
      width: auto;

      @media (min-width: 768px) {
        height: 31px;
      }
      @media (min-width: 1440px) {
        height: 44px;
      }
    }

    img:first-child {
      width: auto;
      height: 25px;
      @media (min-width: 768px) {
        height: 21px;
      }
    }
  }

  a {
    ${StyledA}
    padding-right: 16px !important;

    &:before {
      right: -18px;
      top: 1px;
    }
  }
`
export const StyledTrustedCta = styled(StyledSection)`
  display: flex;
  justify-content: center;
  margin: 15% 0 5% 0;
  grid-row: 2/2;
  grid-column: 1 / 4;

  @media (min-width: 768px) {
    margin: 2.8% 0 5% 0;
  }

  @media (min-width: 1024px) {
    margin: 1.8% 0 2% 0;
  }
`

/* Transform */
export const StyledTransform = styled(StyledSection)`
  margin-bottom: 50px;
`

export const StyledIntroTransform = styled.div`
  margin-bottom: 16px;
  width: 90%;

  p {
    @media (min-width: 1024px) {
      width: 100%;
    }
  }
`
export const StyledTransformContent = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;

  p {
    text-align: center;
    margin-bottom: 26px;
    width: 86%;

    @media (min-width: 768px) {
      width: 63%;
    }
  }

  a {
    ${StyledA}
  }
`

/* Testimonials */
export const StyledTestimonial = styled(StyledSection)`
  margin-bottom: 117px;
`

/* Contact */
export const StyledContact = styled(StyledSection)`
  position: relative;
  padding-bottom: 63px;

  @media (min-width: 1024px) {
    padding-bottom: 0px;
  }

  .m-dark {
    background-color: var(--magenta-darker);
    position: absolute;
    width: 100%;
    height: 162px;
    bottom: -2px;

    @media (min-width: 1024px) {
      height: 129px;
    }
  }
`
export const StyledContactRefs = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  margin: 5rem 0 1rem;

  @media (min-width: 420px) {
    margin-top: 1rem;
  }

  @media (min-width: 768px) {
    grid-template-columns: unset;
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    max-width: 80%;
  }

  @media (min-width: 1024px) {
    max-width: 900px;
  }

  p {
    margin-bottom: 0.3rem;
    color: var(--grey-50);
    font: var(--text-sm);

    > span {
      display: inline;

      &.grey-icon {
        padding-left: 20px;
        padding-right: 5px;
      }

      > span.grey-icon {
        padding-left: 20px;
        padding-right: 5px;
      }

      > span:not(.grey-icon) {
        flex: 1;
      }
    }
  }

  p:nth-child(1) {
    grid-column: 1/2;
    grid-row: 1/1;

    @media (min-width: 768px) {
      grid-column: unset;
      grid-row: unset;
    }
  }

  p:nth-child(2) {
    grid-column: 1/2;
    grid-row: 2/2;

    @media (min-width: 768px) {
      grid-column: unset;
      grid-row: unset;
    }
  }

  p:nth-child(3) {
    grid-column: 1/2;
    grid-row: 3/3;

    @media (min-width: 768px) {
      grid-column: unset;
      grid-row: unset;
    }
  }

  p:nth-child(4) {
    grid-column: 2/3;
    grid-row: 2/2;

    @media (min-width: 768px) {
      grid-column: unset;
    }
  }

  p:nth-child(5) {
    grid-column: 2/3;
    grid-row: 3/3;

    @media (min-width: 768px) {
      grid-column: unset;
      grid-row: unset;
    }
  }
`

export const NetworkMapFootNotes = styled(StyledContactRefs)`
  margin: 0 auto ${({ theme }) => theme.space.md};
`

export const StyledContactCard = styled.div`
  background-color: var(--blue-light);
  border-radius: 16px;
  width: 100%;
  height: 467px;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-direction: column;
  padding: 92px 0;

  @media (min-width: 550px) {
    width: 80%;
    height: 414px;
  }

  @media (min-width: 1024px) {
    width: 100%;
    max-width: 1134px;
    height: 422px;
    padding: 117px 0 97px 0;
  }
`
export const StyledContactCardTitle = styled.div`
  width: 70%;
  max-width: 468px;

  @media (min-width: 768px) {
    width: 79%;
  }

  @media (min-width: 1024px) {
    width: 100%;
    max-width: 962px;
  }

  p {
    display: block;
    font-weight: 800;
    font-size: 45px;
    line-height: 110%;
    text-align: center;
    letter-spacing: -0.336px;
    color: var(--grey-90);

    @media (min-width: 1024px) {
      font-size: 57px;
    }
  }
`
export const StyledContactCardButtons = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-direction: column;
  position: relative;

  a:first-of-type {
    margin-bottom: 1rem;
  }
`
